import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useSelector } from '@/store/utils';
import type { BookingDTO } from '@/types/dto/agent';
import { useMemo } from 'react';

/**
 * Resolves the booking the checkout should operate on:
 *   - regular purchase → `purchase.booking`
 *   - change-booking   → the original `manage.booking`
 *
 * In change-booking mode the booking carries BOTH the original trips and
 * the newly exchanged-in trips in `bookedTrips[]`. The ids of the new
 * trips live in `exchangeOperations[].bookedTrips[]`. We filter
 * `bookedTrips` down to those ids so seat-selection / extras operate on
 * the NEW trips, not the originals — mirroring agent-tool's
 * `currentTripsSelector`. Everything else on the booking (passengers,
 * purchaser, id, prices) is preserved.
 *
 * When no exchange operation exists yet, the booking is returned as-is.
 */
export const useCheckoutBooking = (): BookingDTO | null => {
  const purchaseBooking = useSelector(purchaseFlowBookingSelector);
  const manageBooking = useSelector(manageFlowBookingSelector);
  const { hasSelection } = useChangeBookingSelection();

  return useMemo(() => {
    const booking = hasSelection ? manageBooking : purchaseBooking;
    if (!booking) return booking;

    const exchangeTripIds = (booking.exchangeOperations ?? []).flatMap((op) =>
      (op.bookedTrips ?? []).map((trip) => trip.id)
    );
    if (exchangeTripIds.length === 0) return booking;

    return {
      ...booking,
      bookedTrips: (booking.bookedTrips ?? []).filter((trip) =>
        exchangeTripIds.includes(trip.id)
      ),
    };
  }, [hasSelection, manageBooking, purchaseBooking]);
};
